import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Form, Col, Row } from 'react-bootstrap'
import { Editor } from '@tinymce/tinymce-react'
import tinymce from 'tinymce/tinymce'
import { DesignContext } from 'contexts/DesignContext'
import objectPath from 'object-path'
import './style.css'
import Label from '../Label'
import { createUischemaStyle } from '../Style'

const RichTextEditor = (props) => {
  const designContext = useContext(DesignContext)
  const {
    jsonForm,
    designerProps,
    elementAction = {},
    setFieldFormData,
    setFieldAllData,
  } = designContext
  let formData = jsonForm?.formData || {}
  const { uischema, config, fieldName, fieldKey } = props
  const {
    isDynamic = false,
    dynamicName = '',
    dynamicKey = '',
    dynamicIndex = 0,
    dynamicData = {},
    onUpdateFormData = () => {},
    isArray,
    arrayName,
    arrayKey,
  } = config
  let { key, name, pattern } = uischema
  const [label, setLabel] = useState(null)

  const uiKey = useMemo(() => {
    if (isDynamic) {
      if (designerProps) {
        let elementName = designerProps.filter(
          ({ propType }) => propType === 'name'
        )
        let uiName = elementName.find(
          ({ prop }) => `@${prop}` === uischema.name
        )
        if (uiName) {
          setLabel(uiName.systemValue)
          return uiName.systemName
        }
      }
      return uischema.name
    }
    return uischema.key
  }, [isDynamic])

  const uiName = useMemo(() => {
    if (isDynamic) {
      if (designerProps) {
        let elementName = designerProps.filter(
          ({ propType }) => propType === 'name'
        )
        let uiName = elementName.find(
          ({ prop }) => `@${prop}` === uischema.name
        )
        if (uiName) {
          setLabel(uiName.systemValue)
          return uiName.systemName
        }
      }
    }
    if (isArray) {
      return `@${arrayName}.${uischema.name}`
    }
    return uischema.name
  }, [isDynamic])

  const [render, setRender] = useState(false)

  let datasource = uischema?.datasource || []
  const jsonFormRef = useRef(jsonForm)
  const datasourceRef = useRef(datasource)
  jsonFormRef.current = jsonForm
  datasourceRef.current = datasource

  const getDatasourceSourceName = (datasource = {}) => {
    if (datasource?.isReceiveProp) {
      return datasource?.sourceName || ''
    }

    let sourceKey = datasource?.sourceKey
    let source = objectPath.get(
      jsonFormRef.current,
      `datasources.${sourceKey}`
    )
    if (source?.isReceiveProp) {
      return source.name || datasource?.sourceName || ''
    }

    if (source?.name) {
      return source.name === 'QueryParam' || source.name === 'PathParam'
        ? source.name
        : `API > ${source.name}`
    }

    return datasource?.sourceName || ''
  }

  let editorValue = useMemo(() => {
    let formValue = null
    if (isDynamic) {
      formValue = objectPath.get(
        formData,
        `${dynamicName}.${dynamicIndex}.${name}`
      )
    } else {
      formValue = objectPath.get(formData, key)
    }
    return formValue
  }, [render])

  const isLocked = objectPath.get(elementAction, ['isLockedEdit'])
  let hasEnable = objectPath.has(elementAction, ['isEnable', key])
  let isEnable = objectPath.get(elementAction, ['isEnable', key])
  let hasRequire = objectPath.has(elementAction, ['isRequire', key])
  let requireMessage = objectPath.get(elementAction, ['isRequire', key])
  let showRequireMessage = hasRequire
    ? typeof requireMessage !== 'boolean'
    : require
  let isRequire = hasRequire || require
  let showRequire = editorValue ? false : isRequire ? true : false

  let isReadOnly = useMemo(() => {
    if (isLocked) return isLocked
    if (hasEnable) {
      return isEnable === false ? true : false
    }
    return false
  }, [isEnable])

  const uiStyle = useMemo(
    () => createUischemaStyle({ uischema, showRequire }),
    [uischema, showRequire]
  )

  const updateData = (value) => {
    if (isDynamic) {
      objectPath.set(dynamicData, uiKey, value)
      setFieldFormData(`${dynamicName}.${dynamicIndex}.${uiKey}`, value)
      setFieldAllData(`${dynamicName}.${dynamicIndex}.${uiKey}`, value)
      onUpdateFormData(`${dynamicName}.${dynamicIndex}.${uiKey}`, value)
    } else {
      setFieldFormData(uiKey, value)
      setFieldFormData(uiName, value)
      setFieldAllData(uiKey, value)
      setFieldAllData(uiName, value)
      onUpdateFormData(uiName, value)
    }
    setRender(true)
  }

  useEffect(() => {
    if (render) {
      setRender(false)
    }
  }, [render])

  useEffect(() => {
    if (pattern && editorValue === undefined) {
      updateData(pattern)
    }
  }, [pattern])

  return (
    <Form.Group className="mb-0" controlId={uischema?.controlId}>
      <Row className="w-100 mx-0">
        <Label
          {...props}
          uischema={uischema}
          label={label}
          isEnable={!isReadOnly}
        />
        <Col
          sm={uischema?.elementWidth || 12}
          className="mt-1 p-0"
          style={uiStyle}
        >
          <Editor
            apiKey="ijcrie2wo48kbeqad77tg82er0plrv3lwcggmiaww55zd8s4"
            disabled={isReadOnly}
            init={{
              height: uischema?.maxHeight || 400,
              menubar: true,
              plugins: [
                'print preview paste importcss',
                'searchreplace autolink autosave',
                'save directionality code visualblocks',
                'visualchars fullscreen image link',
                'media template codesample table',
                'charmap hr pagebreak nonbreaking',
                'anchor toc insertdatetime advlist',
                'lists wordcount imagetools textpattern',
                'noneditable help charmap quickbars emoticons',
              ],
              toolbar: [
                'undo redo',
                'addDataSource',
                'formatselect',
                'bold italic forecolor backcolor',
                'link image',
                'alignleft aligncenter',
                'alignright alignjustify',
                'bullist numlist outdent indent',
                'table tabledelete',
                'tableprops tablerowprops tablecellprops',
                'tableinsertrowbefore tableinsertrowafter tabledeleterow',
                'tableinsertcolbefore tableinsertcolafter tabledeletecol',
                'removeformat',
              ].join(' | '),
              image_title: true,
              automatic_uploads: true,
              file_picker_types: 'image',
              file_picker_callback: function (cb, value, meta) {
                var input = document.createElement('input')
                input.setAttribute('type', 'file')
                input.setAttribute('accept', 'image/*')
                input.onchange = function () {
                  var file = this.files[0]
                  var reader = new FileReader()
                  reader.onload = function () {
                    var id = 'blobid' + new Date().getTime()
                    var blobCache = tinymce.activeEditor.editorUpload.blobCache
                    var base64 = reader.result.split(',')[1]
                    var blobInfo = blobCache.create(id, file, base64)
                    blobCache.add(blobInfo)
                    cb(blobInfo.blobUri(), { title: file.name })
                  }
                  reader.readAsDataURL(file)
                }
                input.click()
              },
              content_style:
                'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
              setup: function (editor) {
                editor.ui.registry.addMenuButton('addDataSource', {
                  text: 'Datasource',
                  fetch: function (callback) {
                    let datasource = datasourceRef.current || []
                    let items = []
                    let groupList = []
                    let datasourceGroup = datasource.filter(
                      (source) => {
                        let { dataType } = source || {}
                        let sourceName = getDatasourceSourceName(source)
                        if (dataType === 'datasource') {
                          if (!groupList.includes(sourceName)) {
                            groupList.push(sourceName)
                          }
                          return true
                        }
                        return false
                      }
                    )
                    let datasourceItem = datasource.filter(
                      ({ dataType }) => dataType !== 'datasource'
                    )
                    groupList.sort((a, b) => {
                      return a.localeCompare(b)
                    })
                    // item data source
                    datasourceItem.forEach(({ name }) => {
                      let item = {
                        type: 'menuitem',
                        text: name,
                        onAction: () => {
                          editor.insertContent(`<code>@${name}</code>`)
                        },
                      }
                      items.push(item)
                    })
                    // group data source
                    groupList.forEach((sourceName) => {
                      let filterSource = datasourceGroup.filter(
                        (source) =>
                          getDatasourceSourceName(source) === sourceName
                      )
                      let subItems = []
                      filterSource.sort((a, b) => {
                        let aName = a?.name || ''
                        let bName = b?.name || ''
                        return aName.localeCompare(bName)
                      })
                      filterSource.forEach(({ name }) => {
                        subItems.push({
                          type: 'menuitem',
                          text: name,
                          onAction: () => {
                            editor.insertContent(`<code>@${name}</code>`)
                          },
                        })
                      })
                      let mainItem = {
                        type: 'nestedmenuitem',
                        text: sourceName,
                        getSubmenuItems: () => subItems,
                      }
                      items.push(mainItem)
                    })

                    callback(items)
                  },
                })
              },
            }}
            value={editorValue}
            onEditorChange={updateData}
          />
        </Col>
      </Row>
    </Form.Group>
  )
}

export default RichTextEditor
